import React, { useState, useEffect } from "react";
import axios from "axios";
import { Button, Modal, Radio, RadioGroup, Spinner, Tabs, TextInput } from "@opendate-app/setlist";
import { toastError, toastSuccess } from "../../../../shared/toastHelper";

import { useTemplateAutosave } from "../../../../shared/useTemplateAutosave";
import { useCustomCampaignShowContext } from "../CustomCampaignShowContainer";
import { BeefreeComposerTemplatableProvider, useBeefreeComposerTemplatableContext } from "./BeefreeComposerTemplatableContext";
import TemplateEditor from "../../BeefreeTemplates/TemplateEditor";
import ErrorsContainer from "../../BeefreeTemplates/ErrorsContainer";
import ContentTabHeader from "./ContentTabHeader";
import PreviewTab from "./PreviewTab";
import SendTestOverlay from "../../BeefreeTemplates/SendTestOverlay";
import ContentSelectTabs from "./ContentSelectTabs";
import TableViewApp from "../../../../bundles/TableView/startup/TableViewApp";

const composerContainerStyle = {
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100vh",
  backgroundColor: "var(--sl-bg-page)",
  padding: 24,
  gap: 12,
  display: "flex",
  flexDirection: "column",
};

const headerRowStyle = {
  display: "flex",
  flexWrap: "wrap",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
};

const titleStyle = {
  fontSize: 14,
  fontWeight: 600,
  color: "var(--sl-text-primary)",
};

const actionsStyle = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "center",
  gap: 16,
};

const tabsStyle = {
  flex: "1 1 320px",
  minWidth: 0,
};

const tabRowActionsStyle = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  gap: 12,
};

const loadingStyle = {
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  height: "50vh",
};

const COMPOSER_TABS = ["Content", "Preview", "Sent"].map((value) => ({ value, label: value }));

export default () => {
  const {
    csrfToken,
    team,
    customCampaign,
    setCustomCampaign,
    selectedNode,
    setSelectedNode,
    selectedNodeFormState,
    setShowBeefreeComposer,
    user,
    customCampaignBaseUrl,
  } = useCustomCampaignShowContext();

  const [tab, setTab] = useState(selectedNode?.beefree_template ? "Content" : "Opendate Blueprints");
  const [template, setTemplate] = useState(selectedNode?.beefree_template?.template);
  const [subject, setSubject] = useState(selectedNode?.node_data?.subject || "");
  const [preheader, setPreheader] = useState(selectedNode?.node_data?.preheader || "");
  const [processedTemplate, setProcessedTemplate] = useState(template);
  const [errors, setErrors] = useState([]);
  const [saveDisabled, setSaveDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [showBlueprintModal, setShowBlueprintModal] = useState(false);
  const [blueprintName, setBlueprintName] = useState("");
  const [blueprintMode, setBlueprintMode] = useState("new");
  const [blueprintSaving, setBlueprintSaving] = useState(false);

  useEffect(() => {
    setSaveDisabled(processedTemplate === selectedNode?.beefree_template?.template);
  }, [processedTemplate]);

  useEffect(() => {
    if (selectedNode?.beefree_template) {
      setTab("Content");
      setTemplate(selectedNode.beefree_template.template);
      setProcessedTemplate(selectedNode.beefree_template.template);
    }
  }, [selectedNode]);

  const updateWorkflowNodeData = (newData, onSuccess) => {
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
      workflow_node: {
        node_data: {
          ...selectedNode.node_data,
          ...newData,
        },
      },
    })
      .then(({ data: { workflow_node, custom_campaign } }) => {
        setCustomCampaign(custom_campaign);
        setSelectedNode(workflow_node);
        toastSuccess("Email updated successfully.");
        onSuccess?.();
      })
      .catch(() => {
        toastError("Unable to update email.");
      });
  }

  const updateBeefreeTemplate = (beefreeTemplateAttributes) => {
    // Kill any queued/in-flight autosave first so it can't land after this
    // manual write and revert it.
    cancelAutosave();
    setIsLoading(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
      workflow_node: {
        beefree_template_attributes: beefreeTemplateAttributes,
      },
    })
      .then(({ data: { workflow_node, custom_campaign } }) => {
        setCustomCampaign(custom_campaign);
        setSelectedNode(workflow_node);
        setTemplate(workflow_node.beefree_template?.template);
        setProcessedTemplate(workflow_node.beefree_template?.template);
        setIsLoading(false);
        setSaveDisabled(true);
        toastSuccess("Email updated successfully.");
      })
      .catch(() => {
        toastError("Unable to update email.");
        setIsLoading(false);
      });
  };

  // Silent autosave. Same PATCH shape as the manual Save (beefree_template_attributes:
  // { id, template }) but WITHOUT the setCustomCampaign/setSelectedNode/setTemplate
  // the manual handler does on success — those feed processedTemplate/selectedNode
  // back into effects that re-init the Beefree SDK and would tear down the editor
  // mid-composition. The <ContentSelectTabs> gate guarantees beefree_template
  // exists before the editor (and thus this autosave) can ever fire.
  const { isAutosaving, autosave: autosaveTemplate, cancel: cancelAutosave } = useTemplateAutosave((templateData, cancelToken) => {
    if (!selectedNode?.beefree_template?.id) return Promise.resolve();

    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;
    return axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
      workflow_node: {
        beefree_template_attributes: {
          id: selectedNode.beefree_template.id,
          template: templateData,
        },
      },
    }, { cancelToken });
  });

  const copiedFromBlueprint = selectedNode?.beefree_template?.copied_from_blueprint;
  const canUpdateExisting = copiedFromBlueprint && copiedFromBlueprint.team_id === team.id;

  const handleSaveBlueprint = () => {
    setBlueprintSaving(true);
    axios.defaults.headers.common["X-CSRF-TOKEN"] = csrfToken;

    if (blueprintMode === "new") {
      axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
        workflow_node: {
          beefree_template_attributes: {
            id: selectedNode?.beefree_template?.id,
            template: processedTemplate,
          },
        },
      })
        .then(({ data: { workflow_node, custom_campaign } }) => {
          setCustomCampaign(custom_campaign);
          setSelectedNode(workflow_node);
          setTemplate(workflow_node.beefree_template?.template);
          setProcessedTemplate(workflow_node.beefree_template?.template);
          setSaveDisabled(true);

          return axios.post(`/teams/${team.id}/beefree_templates.json`, {
            beefree_template: {
              name: blueprintName,
              template: processedTemplate,
            }
          });
        })
        .then(({ data: blueprintData }) => {
          return axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
            workflow_node: {
              beefree_template_attributes: {
                id: selectedNode?.beefree_template?.id,
                copied_from_blueprint_id: blueprintData.id,
              },
            },
          }).then(({ data: { workflow_node, custom_campaign } }) => {
            setCustomCampaign(custom_campaign);
            setSelectedNode(workflow_node);
            toastSuccess("Blueprint created successfully");
            setBlueprintSaving(false);
            setShowBlueprintModal(false);
          });
        })
        .catch((error) => {
          const errors = error.response?.data?.errors || ["Could not create blueprint"];
          toastError(errors.join(", "));
          setBlueprintSaving(false);
        });
    } else if (blueprintMode === "update" && canUpdateExisting) {
      axios.patch(`${customCampaignBaseUrl}/workflow_nodes/${selectedNode.internal_id}`, {
        workflow_node: {
          beefree_template_attributes: {
            id: selectedNode?.beefree_template?.id,
            template: processedTemplate,
          },
        },
      })
        .then(({ data: { workflow_node, custom_campaign } }) => {
          setCustomCampaign(custom_campaign);
          setSelectedNode(workflow_node);
          setTemplate(workflow_node.beefree_template?.template);
          setProcessedTemplate(workflow_node.beefree_template?.template);
          setSaveDisabled(true);

          return axios.put(`/teams/${team.id}/beefree_templates/${copiedFromBlueprint.id}.json`, {
            beefree_template: {
              template: processedTemplate,
            }
          });
        })
        .then(() => {
          toastSuccess("Blueprint updated successfully");
          setBlueprintSaving(false);
          setShowBlueprintModal(false);
        })
        .catch((error) => {
          const errors = error.response?.data?.errors || ["Could not update blueprint"];
          toastError(errors.join(", "));
          setBlueprintSaving(false);
        });
    }
  };

  const openBlueprintModal = () => {
    setBlueprintName("");
    setBlueprintMode(canUpdateExisting ? "update" : "new");
    setShowBlueprintModal(true);
  };

  const closeBlueprintModal = () => {
    setShowBlueprintModal(false);
  };

  return (
    <BeefreeComposerTemplatableProvider
      template={template}
      setTemplate={setTemplate}
      processedTemplate={processedTemplate}
      setProcessedTemplate={setProcessedTemplate}
      errors={errors}
      setErrors={setErrors}
      sampleData={selectedNode?.node_data_options?.sample_data}
      isEmailTemplate={true}
      beefreeTemplate={selectedNode?.beefree_template}
      user={user}
      testModalOpen={testModalOpen}
      setTestModalOpen={setTestModalOpen}
      subject={subject}
      preheader={preheader}
    >
      <SendTestOverlay templatableContext={useBeefreeComposerTemplatableContext} />
      <div style={composerContainerStyle}>
        <div style={headerRowStyle}>
          <div style={titleStyle}>
            Email
          </div>
          <div style={actionsStyle}>
            <Button variant="link-color" onClick={(e) => {
              e.preventDefault();
              setShowBeefreeComposer(false);
            }}>
              Back to workflow
            </Button>
            {selectedNode?.beefree_template ? (
              <>
                <Button onClick={openBlueprintModal}>
                  Save as Blueprint
                </Button>
                <Button
                  variant="primary"
                  disabled={saveDisabled || isLoading || isAutosaving}
                  onClick={() => updateBeefreeTemplate({ id: selectedNode?.beefree_template?.id, template: processedTemplate })}
                >
                  {isAutosaving ? "Saving…" : "Save"}
                </Button>
              </>
            ) : null}
          </div>
        </div>
        {selectedNode?.beefree_template ? (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <div style={headerRowStyle}>
              <div style={tabsStyle}>
                <Tabs collapseBelow={0} aria-label="Email sections" value={tab} onChange={setTab} items={COMPOSER_TABS} />
              </div>
              <div style={tabRowActionsStyle}>
                <ErrorsContainer templatableContext={useBeefreeComposerTemplatableContext} />
                <Button variant="secondary-color" onClick={() => setTestModalOpen(true)}>
                  Send Test
                </Button>
              </div>
            </div>
            {tab === "Content" && template ? (
              <div style={{ boxShadow: "var(--sl-shadow-sm)" }}>
                <ContentTabHeader
                  updateWorkflowNodeData={updateWorkflowNodeData}
                  updateBeefreeTemplate={updateBeefreeTemplate}
                  subject={subject}
                  setSubject={setSubject}
                  preheader={preheader}
                  setPreheader={setPreheader}
                />
                <TemplateEditor
                  templatableContext={useBeefreeComposerTemplatableContext}
                  autosave
                  onAutosave={autosaveTemplate}
                  mergeTags={selectedNodeFormState?.node_data_options?.merge_tags}
                />
              </div>
            ) : tab === "Preview" ? (
              <PreviewTab
                template={template}
                sampleData={selectedNodeFormState?.node_data_options?.sample_data}
              />
            ) : tab === "Sent" ? (
              <TableViewApp
                title="Emails"
                dataSource={`/teams/${team.id}/ahoy_messages.json?trackable_type[]=WorkflowNode&trackable_id=${selectedNode.internal_id}`}
                order={[[0, "desc"]]}
                columns={[
                  {label: "Date Sent", data: "created_at"},
                  {label: "Subject", data: "subject", orderable: false},
                  {label: "Recipient", data: "recipient", orderable: false},
                  {label: "Status", data: "status", orderable: false},
                ]}
              />
            ) : null}
          </div>
        ) : isLoading ? (
          <div style={loadingStyle}>
            <Spinner size="md" aria-label="Loading email" />
          </div>
        ) : (
          <ContentSelectTabs
            updateBeefreeTemplate={updateBeefreeTemplate}
            tab={tab}
            setTab={setTab}
          />
        )}
      </div>
      <Modal
        open={showBlueprintModal}
        onClose={closeBlueprintModal}
        width={480}
        title="Save as Blueprint"
        description="Save this template as a reusable blueprint for your team."
        closeOnBackdrop={!blueprintSaving}
        closeOnEsc={!blueprintSaving}
        showClose={!blueprintSaving}
        footer={(
          <>
            <Button variant="secondary" onClick={closeBlueprintModal} disabled={blueprintSaving}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSaveBlueprint}
              disabled={blueprintSaving || (blueprintMode === "new" && !blueprintName.trim())}
            >
              {blueprintSaving ? "Saving..." : blueprintMode === "new" ? "Create Blueprint" : "Update Blueprint"}
            </Button>
          </>
        )}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {canUpdateExisting && (
            <div role="radiogroup" aria-label="Blueprint" style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              <RadioGroup name="blueprintMode" value={blueprintMode} onChange={setBlueprintMode} disabled={blueprintSaving}>
                <Radio value="update" label={<>Update existing blueprint: <strong>{copiedFromBlueprint?.name}</strong></>} />
                <Radio value="new" label="Save as new blueprint" />
              </RadioGroup>
            </div>
          )}
          {blueprintMode === "new" && (
            <TextInput
              label="Blueprint Name"
              placeholder="Enter blueprint name"
              value={blueprintName}
              onChange={(e) => setBlueprintName(e.target.value)}
              disabled={blueprintSaving}
              autoFocus
            />
          )}
        </div>
      </Modal>
    </BeefreeComposerTemplatableProvider>
  );
};
